import { useState, useMemo } from "react";
import type { PrinterProfile } from "../lib/data";
import { MapPin } from "lucide-react";

interface MapViewProps {
	printers: PrinterProfile[];
	userLocation: { lat: number; lng: number } | null;
	selectedPrinter: string | null;
	onSelectPrinter: (id: string) => void;
}

function latLngToPixel(
	lat: number,
	lng: number,
	bounds: { minLat: number; maxLat: number; minLng: number; maxLng: number },
	width: number,
	height: number
) {
	const x = ((lng - bounds.minLng) / (bounds.maxLng - bounds.minLng)) * width;
	const y = ((bounds.maxLat - lat) / (bounds.maxLat - bounds.minLat)) * height;
	return { x, y };
}

export function MapView({ printers, userLocation, selectedPrinter, onSelectPrinter }: MapViewProps) {
	const [hoveredPrinter, setHoveredPrinter] = useState<string | null>(null);

	const bounds = useMemo(() => ({
		minLat: 37.74,
		maxLat: 37.81,
		minLng: -122.46,
		maxLng: -122.38,
	}), []);

	const width = 800;
	const height = 500;

	return (
		<div className="relative w-full rounded-xl overflow-hidden border border-border bg-muted/30">
			<svg
				viewBox={`0 0 ${width} ${height}`}
				className="w-full h-auto"
				style={{ minHeight: "350px" }}
			>
				<defs>
					<linearGradient id="mapGrad" x1="0%" y1="0%" x2="100%" y2="100%">
						<stop offset="0%" stopColor="hsl(210 40% 96%)" />
						<stop offset="100%" stopColor="hsl(210 30% 92%)" />
					</linearGradient>
					<linearGradient id="waterGrad" x1="0%" y1="0%" x2="100%" y2="100%">
						<stop offset="0%" stopColor="hsl(210 60% 85%)" />
						<stop offset="100%" stopColor="hsl(210 50% 80%)" />
					</linearGradient>
					<filter id="pinShadow" x="-50%" y="-50%" width="200%" height="200%">
						<feDropShadow dx="0" dy="2" stdDeviation="3" floodOpacity="0.3" />
					</filter>
					<filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
						<feGaussianBlur stdDeviation="4" result="blur" />
						<feMerge>
							<feMergeNode in="blur" />
							<feMergeNode in="SourceGraphic" />
						</feMerge>
					</filter>
				</defs>

				{/* Map background */}
				<rect width={width} height={height} fill="url(#mapGrad)" />

				{/* Water areas */}
				<path d="M0 0 L200 0 L150 120 L100 200 L0 250 Z" fill="url(#waterGrad)" opacity="0.5" />
				<path d={`M${width - 100} 0 L${width} 0 L${width} 150 L${width - 60} 100 Z`} fill="url(#waterGrad)" opacity="0.5" />
				<path d={`M0 ${height - 80} L120 ${height - 120} L250 ${height} L0 ${height} Z`} fill="url(#waterGrad)" opacity="0.4" />

				{/* Grid lines for streets */}
				{Array.from({ length: 12 }).map((_, i) => (
					<line
						key={`vl-${i}`}
						x1={70 + i * 60}
						y1={20}
						x2={70 + i * 60}
						y2={height - 20}
						stroke="hsl(210 20% 88%)"
						strokeWidth="1"
						opacity="0.5"
					/>
				))}
				{Array.from({ length: 8 }).map((_, i) => (
					<line
						key={`hl-${i}`}
						x1={20}
						y1={60 + i * 55}
						x2={width - 20}
						y2={60 + i * 55}
						stroke="hsl(210 20% 88%)"
						strokeWidth="1"
						opacity="0.5"
					/>
				))}

				{/* Major roads */}
				<line x1={250} y1={0} x2={250} y2={height} stroke="hsl(210 15% 82%)" strokeWidth="3" opacity="0.6" />
				<line x1={500} y1={0} x2={500} y2={height} stroke="hsl(210 15% 82%)" strokeWidth="3" opacity="0.6" />
				<line x1={0} y1={250} x2={width} y2={250} stroke="hsl(210 15% 82%)" strokeWidth="3" opacity="0.6" />

				{/* Park areas */}
				<rect x={300} y={150} width={120} height={80} rx={8} fill="hsl(140 30% 88%)" opacity="0.6" />
				<rect x={100} y={320} width={90} height={60} rx={8} fill="hsl(140 30% 88%)" opacity="0.6" />

				{/* User location */}
				{userLocation && (() => {
					const pos = latLngToPixel(userLocation.lat, userLocation.lng, bounds, width, height);
					return (
						<g>
							<circle cx={pos.x} cy={pos.y} r={30} fill="hsl(220 90% 56%)" opacity="0.1">
								<animate attributeName="r" values="20;35;20" dur="3s" repeatCount="indefinite" />
								<animate attributeName="opacity" values="0.15;0.05;0.15" dur="3s" repeatCount="indefinite" />
							</circle>
							<circle cx={pos.x} cy={pos.y} r={15} fill="hsl(220 90% 56%)" opacity="0.15">
								<animate attributeName="r" values="12;20;12" dur="3s" repeatCount="indefinite" />
							</circle>
							<circle cx={pos.x} cy={pos.y} r={7} fill="hsl(220 90% 56%)" stroke="white" strokeWidth="2.5" />
						</g>
					);
				})()}

				{/* Printer pins */}
				{printers.map((printer) => {
					const pos = latLngToPixel(printer.location.lat, printer.location.lng, bounds, width, height);
					const isSelected = selectedPrinter === printer.id;
					const isHovered = hoveredPrinter === printer.id;
					const scale = isSelected ? 1.3 : isHovered ? 1.15 : 1;

					return (
						<g
							key={printer.id}
							transform={`translate(${pos.x}, ${pos.y})`}
							onClick={() => onSelectPrinter(printer.id)}
							onMouseEnter={() => setHoveredPrinter(printer.id)}
							onMouseLeave={() => setHoveredPrinter(null)}
							className="cursor-pointer"
							filter="url(#pinShadow)"
						>
							<g transform={`scale(${scale})`} style={{ transformOrigin: "0 0", transition: "transform 0.2s ease" }}>
								{/* Pin shape */}
								<path
									d="M0,-28 C-8,-28 -14,-22 -14,-14 C-14,-6 0,4 0,4 C0,4 14,-6 14,-14 C14,-22 8,-28 0,-28 Z"
									fill={isSelected ? "hsl(220 90% 50%)" : isHovered ? "hsl(220 80% 55%)" : "hsl(0 0% 15%)"}
									stroke="white"
									strokeWidth="2"
								/>
								{/* Inner circle with rating */}
								<circle cx={0} cy={-14} r={6} fill="white" />
								<text
									x={0}
									y={-11}
									textAnchor="middle"
									fontSize="7"
									fontWeight="700"
									fill={isSelected ? "hsl(220 90% 50%)" : "hsl(0 0% 15%)"}
								>
									{printer.rating.toFixed(1)}
								</text>
							</g>

							{/* Tooltip on hover */}
							{(isHovered || isSelected) && (
								<g transform="translate(0, -45)">
									<rect
										x={-75}
										y={-35}
										width={150}
										height={32}
										rx={6}
										fill="hsl(0 0% 10%)"
										opacity="0.95"
									/>
									<text x={0} y={-17} textAnchor="middle" fontSize="11" fontWeight="600" fill="white">
										{printer.name}
									</text>
									<text x={0} y={-6} textAnchor="middle" fontSize="9" fill="hsl(0 0% 70%)">
										{printer.printerModel} · {printer.materials.length} materials
									</text>
								</g>
							)}
						</g>
					);
				})}

				{/* Map label */}
				<text x={width - 15} y={height - 10} textAnchor="end" fontSize="10" fill="hsl(210 10% 70%)" fontWeight="500">
					San Francisco Bay Area
				</text>
			</svg>

			{/* Floating legend */}
			<div className="absolute top-3 left-3 bg-background/90 backdrop-blur-sm rounded-lg border border-border px-3 py-2 text-xs space-y-1">
				<div className="flex items-center gap-2">
					<div className="w-3 h-3 rounded-full bg-blue-500 border-2 border-white" />
					<span className="text-muted-foreground">Your location</span>
				</div>
				<div className="flex items-center gap-2">
					<div className="w-3 h-3 rounded-full bg-foreground border-2 border-white" />
					<span className="text-muted-foreground">Verified printers</span>
				</div>
			</div>

			{/* Printer count */}
			<div className="absolute top-3 right-3 bg-background/90 backdrop-blur-sm rounded-lg border border-border px-3 py-2">
				<div className="flex items-center gap-1.5 text-xs font-medium">
					<MapPin className="w-3.5 h-3.5 text-primary" />
					{printers.length} printers nearby
				</div>
			</div>
		</div>
	);
}
